import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
  IconButton,
  CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import axios from 'axios';
import { trackLead } from './utils/facebookPixel';

function ContactForm({ 
  open, 
  onClose, 
  businessDetails, 
  keyword, 
  averageRank,
  searchId, // Die searchId von der Server-Queue
  onSubmit 
}) {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  
  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    // Validate form
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.phone) {
      setError('Bitte fülle alle Felder aus.');
      setLoading(false);
      return;
    }
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Bitte gib eine gültige E-Mail-Adresse ein.');
      setLoading(false);
      return;
    }
    
    try {
      // Detailliertes Logging zur Fehleranalyse
      console.log('Form submit - verfügbare Daten:');
      console.log(`- businessDetails: ${!!businessDetails} (${typeof businessDetails})`);
      console.log(`- businessDetails?.place_id: ${businessDetails?.place_id}`);
      console.log(`- keyword: "${keyword}"`);
      console.log(`- averageRank: ${averageRank}`);
      
      // Sicherstellen, dass place_id korrekt als placeId übergeben wird
      const placeId = businessDetails?.place_id || '';
      
      // Sende die proaktiven Daten direkt über den search-complete Endpunkt
      // Dadurch wird die Logik zentralisiert und konsistent
      const payload = {
        placeId: placeId,
        keyword: keyword || '',
        averageRank: averageRank || 0,
        contactData: formData, // Die Kontaktdaten mitgeben
        isProactive: true,     // Markieren als proaktive Eintragung
        ctaClicked: true       // CTA wurde geklickt
      };
      
      // Wenn eine searchId vorhanden ist, fügen wir sie hinzu (für die Server-Queue)
      if (searchId) {
        payload.searchId = searchId;
        console.log(`Formular für Suche mit ID ${searchId} abgesendet`);
      }
      
      console.log('Sende Payload:', JSON.stringify(payload));
      
      await axios.post('/api/search-complete', payload);
      
      // Sende Facebook Pixel Lead-Event
      try {
        trackLead(businessDetails, keyword, formData);
        console.log('Facebook Pixel Lead-Event im Frontend gesendet');
      } catch (pixelError) {
        console.error('Fehler beim Senden des Facebook Pixel Lead-Events:', pixelError);
      }
      
      setSuccess(true);
      setLoading(false);
      
      // Call the onSubmit callback to let parent component know submission was successful
      if (onSubmit) {
        onSubmit(true);
      }
      
      // Close dialog after 2 seconds
      setTimeout(() => {
        onClose();
      }, 2000);
      
    } catch (err) {
      console.error('Fehler beim Senden des Formulars:', err);
      setError('Ein Fehler ist aufgetreten. Bitte versuche es später noch einmal.');
      setLoading(false);
    }
  };
  
  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        pb: 1
      }}>
        <Typography variant="h5" component="div" fontWeight={600}>
          🎯 Deine kostenlose Checkliste für Platz #1
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent>
        {success ? (
          <Box sx={{ 
            textAlign: 'center', 
            py: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Box sx={{ 
              width: 60, 
              height: 60, 
              borderRadius: '50%', 
              bgcolor: '#10B98120',
              color: '#10B981',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 2
            }}>
              <span style={{ fontSize: '32px' }}>✓</span>
            </Box>
            <Typography variant="h6" gutterBottom>
              Vielen Dank!
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Wir haben deine Anfrage erhalten und melden uns in Kürze bei dir.
            </Typography>
          </Box>
        ) : (
          <>
            <Typography variant="body1" sx={{ mb: 3, mt: 1, lineHeight: 1.7 }}>
              Erhalte in den nächsten 24 Stunden deine <strong>individuelle, kostenlose Checkliste</strong> mit:
            </Typography>
            <Box sx={{ mb: 3, pl: 2 }}>
              <Typography variant="body2" sx={{ mb: 1, display: 'flex', alignItems: 'flex-start' }}>
                ✅ <span style={{ marginLeft: '8px' }}>Den exakten Schritten, um dein Google Ranking zu verbessern</span>
              </Typography>
              <Typography variant="body2" sx={{ mb: 1, display: 'flex', alignItems: 'flex-start' }}>
                ✅ <span style={{ marginLeft: '8px' }}>Sofort umsetzbare Tipps für mehr Sichtbarkeit</span>
              </Typography>
              <Typography variant="body2" sx={{ mb: 1, display: 'flex', alignItems: 'flex-start' }}>
                ✅ <span style={{ marginLeft: '8px' }}>Insider-Strategien für lokale Dominanz</span>
              </Typography>
              <Typography variant="body2" sx={{ display: 'flex', alignItems: 'flex-start' }}>
                ✅ <span style={{ marginLeft: '8px' }}>Bonus: Kostenloses 15-Min. Strategiegespräch</span>
              </Typography>
            </Box>
            
            <form onSubmit={handleSubmit}>
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <TextField
                  name="firstName"
                  label="Vorname"
                  value={formData.firstName}
                  onChange={handleChange}
                  fullWidth
                  required
                  variant="outlined"
                />
                <TextField
                  name="lastName"
                  label="Nachname"
                  value={formData.lastName}
                  onChange={handleChange}
                  fullWidth
                  required
                  variant="outlined"
                />
              </Box>
              
              <TextField
                name="email"
                label="E-Mail"
                type="email"
                value={formData.email}
                onChange={handleChange}
                fullWidth
                required
                variant="outlined"
                sx={{ mb: 2 }}
              />
              
              <TextField
                name="phone"
                label="Telefon"
                value={formData.phone}
                onChange={handleChange}
                fullWidth
                required
                variant="outlined"
                sx={{ mb: 2 }}
              />
              
              {error && (
                <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                  {error}
                </Typography>
              )}
              
              <DialogActions sx={{ px: 0, pb: 0, pt: 2 }}>
                <Button 
                  type="submit" 
                  variant="contained" 
                  fullWidth
                  disabled={loading}
                  sx={{ 
                    py: 1.5,
                    borderRadius: 2,
                    background: 'linear-gradient(45deg, #10B981 0%, #059669 100%)',
                    boxShadow: '0 4px 10px rgba(16, 185, 129, 0.3)'
                  }}
                >
                  {loading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    '🚀 Checkliste jetzt kostenlos erhalten!'
                  )}
                </Button>
              </DialogActions>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default ContactForm;